import { v4 as uuid } from 'uuid'
// import { ajax } from '../ajax'
import Logger from '../Log'
// import { ajax } from '../ajax'

const Log = Logger.for('AuctionTracker')

/*
 * Bid states.
 *
 * %BID_PENDING: Awaiting response.
 * %BID_TIMEOUT: Response timeout.
 * %BID_NOBID: No-bid response.
 * %BID_SUCCESS: Successful bid.
 */
const BID_PENDING = 0
const BID_TIMEOUT = 1
const BID_NOBID = 2
const BID_SUCCESS = 3

/*
 * Pending bid tracker.
 */
class PendingBid {
  constructor (request) {
    this._bidder = request.bidder
    this._bidId = request.bidId
    this._state = BID_PENDING
    /*
     * Valid if this._state == BID_SUCCESS.
     */
    this._adFormat = null
    this._adWidth = 0
    this._adHeight = 0
    this._adId = null
    this._bidPrice = 0.0
    this._bidWon = false
    this._bidCleared = false
  }

  /*
   * Destination bidder.
   */
  bidder () {
    return this._bidder
  }

  /*
   * Bidder unique id.
   */
  bidId () {
    return this._bidId
  }

  /*
   * Test if we're still waiting on update.
   */
  isPending () {
    return this._state === BID_PENDING
  }

  /*
   * Test if bid has been marked as winning bid.
   */
  isWon () {
    return this._bidWon === true
  }

  /*
   * Mark bid as winning bid.
   * @param cleared true if also cleared already
   */
  won (cleared = null) {
    this._bidWon = true
    if (cleared === true) {
      this._bidCleared = true
    }
  }

  /*
   * Mark bid-response.
   *
   * If @data is %null: timeout
   * If @data.cpm is non-numeric or zero: no-bid
   * otherwise: bid
   */
  update (data) {
    if (data === null) {
      this._state = BID_TIMEOUT
    } else if (typeof data.cpm !== 'number' || data.cpm === 0) {
      this._state = BID_NOBID
    } else {
      this._state = BID_SUCCESS
      if (typeof data.mediaType !== 'string') {
        if (typeof data.vastUrl !== 'undefined' ||
          typeof data.vastXml !== 'undefined') {
          this._adFormat = 'video'
        } else if (typeof data.native !== 'undefined') {
          this._adFormat = 'native'
        } else {
          this._adFormat = 'banner'
        }
      } else {
        this._adFormat = data.mediaType
      }
      this._adWidth = data.width
      this._adHeight = data.height
      this._adId = data.creativeId
      this._bidPrice = data.cpm
    }
  }

  /*
   * Serialize this object to JSON for remote archiving.
   */
  toJson () {
    if (this._state === BID_NOBID) {
      return null
    }

    const json = {
      bidder: this._bidder,
      bidId: this._bidId
    }
    if (this._state === BID_TIMEOUT) {
      json.timeout = true
    } else if (this._state === BID_SUCCESS) {
      json.adFormat = this._adFormat
      json.adWidth = this._adWidth
      json.adHeight = this._adHeight
      json.adId = this._adId
      json.bidPrice = this._bidPrice
      json.bidWon = this._bidWon
      json.bidCleared = this._bidCleared
    }
    return json
  }
}

/*
 * Pending auction tracker.
 */
class PendingAuction {
  constructor (transactionId) {
    this._pendingBids = []
    this._adFormats = []
    this._adSizes = []
    this._transactionId = transactionId
  }

  /*
   * Find pending bid by bid id.
   */
  _findPendingBid (bidId) {
    for (const pendingBid of this._pendingBids) {
      if (pendingBid.bidId() === bidId) {
        return pendingBid
      }
    }
    return null
  }

  /*
   * Add ad format if not present.
   */
  _addAdFormat (format) {
    for (const fmt of this._adFormats) {
      if (fmt === format) {
        return
      }
    }
    this._adFormats.push(format)
  }

  /*
   * Add ad size if not present.
   */
  _addAdSize (size) {
    for (const sz of this._adSizes) {
      if (sz[0] === size[0] && sz[1] === size[1]) {
        return
      }
    }
    this._adSizes.push(size)
  }

  /*
   * Add bid request if it belongs to this auction.
   *
   * Returns %true if bid was added, otherwise, %false.
   */
  addBidRequest (bid) {
    if (bid.transactionId !== this._transactionId) {
      return false
    }

    this._pendingBids.push(new PendingBid(bid))
    if (typeof bid.mediaTypes !== 'undefined') {
      for (const type of Object.keys(bid.mediaTypes)) {
        this._addAdFormat(type)
        if (typeof bid.mediaTypes[type].sizes !== 'undefined') {
          for (const size of bid.mediaTypes[type].sizes) {
            this._addAdSize(size)
          }
        }
      }
    }
    return true
  }

  /*
   * Add bid response if it belongs to this auction.
   *
   * Returns %true if bid was added, otherwise, %false.
   */
  addBidResponse (response) {
    const pbid = this._findPendingBid(response.requestId)
    if (pbid !== null) {
      response.transactionId = this._transactionId
      pbid.update(response)
      return true
    }
    return false
  }

  /*
   * Mark bid response as winning if it belongs to this auction.
   *
   * Returns win URL query upon success, null if failure, smartrtb, or is marked cleared
   */
  markBidWon (response, cleared) {
    const pbid = this._findPendingBid(response.requestId)
    if (pbid !== null) {
      pbid.won(cleared)
      if (pbid.bidder() !== 'smartrtb' && cleared !== true) {
        return 't=' + this._transactionId + '&b=' + pbid.bidder() +
          'i=' + pbid.bidId()
      }
    }

    return null
  }

  /*
   * Add bid response timeout for request if it belongs to this auction.
   *
   * Returns %true if bid was added, otherwise, %false.
   */
  addBidTimeout (request) {
    const pbid = this._findPendingBid(request.bidId)
    if (pbid !== null) {
      pbid.update(null)
      return true
    }
    return false
  }

  /*
   * Finish auction and return resulting JSON for archiving.
   *
   * Returns %null if nothing to archive.
   */
  finish () {
    if (this._pendingBids.length === 0) {
      return null
    }

    const json = {
      adFormats: this._adFormats,
      adSizes: this._adSizes,
      id: this._transactionId,
      bids: []
    }

    for (const pbid of this._pendingBids) {
      if (pbid.isPending()) {
        pbid.update({ cpm: 0 })
      }

      if (pbid.bidder() !== 'smartrtb' || pbid.isWon()) {
        const pbj = pbid.toJson()

        if (pbj !== null) {
          json.bids.push(pbj)
        }
      }
    }
    return json
  }
}

export default class {
  constructor (zoneId) {
    this._zoneId = zoneId
    this._pendingAuctions = {}
    this._auctionInitHandler = (d) => this._onAuctionInit(d)
    this._bidRequestedHandler = (d) => this._onBidRequested(d)
    this._bidResponseHandler = (d) => this._onBidResponse(d)
    this._bidAdjustmentHandler = (d) => this._onBidResponse(d)
    this._bidTimeoutHandler = (d) => this._onBidTimeout(d)
  }

  _onAuctionInit (data) {
    const auctions = this._pendingAuctions[data.auctionId]
    if (auctions === null || typeof auctions === 'undefined') {
      this._pendingAuctions[data.auctionId] = []
    }
  }

  /**
   * Creates new empty auction entry if none was auto created by prebid
   * @returns {*} generated auction id
   */
  initEmptyAuction () {
    const id = uuid()
    this._pendingAuctions[id] = []
    return id
  }

  genTransactionId () {
    return uuid()
  }

  _onBidRequested (data) {
    const auctions = this._pendingAuctions[data.auctionId]
    for (const bid of data.bids) {
      let found = false

      for (const auction of auctions) {
        if (auction.addBidRequest(bid)) {
          found = true
          break
        }
      }

      if (!found) {
        Log.debug('Adding new bid request to event log')
        const auction = new PendingAuction(bid.transactionId)

        auction.addBidRequest(bid)
        auctions.push(auction)
      }
    }
  }

  _onBidResponse (data) {
    const auctions = this._pendingAuctions[data.auctionId]
    for (const auction of auctions) {
      if (auction.addBidResponse(data)) {
        break
      }
    }
  }

  _onBidTimeout (data) {
    const auctions = this._pendingAuctions[data.auctionId]
    for (const auction of auctions) {
      if (auction.addBidTimeout(data)) {
        break
      }
    }
  }

  /*
   * Retrieve remote URL.
   */
  _remoteUrl (what) {
    return '//market-global.smrtb.com/smart-tag/' + what +
           '?z=' + this._zoneId
  }

  /**
   * Mark bid as winning bid.
   *
   * @param bid bid object
   * @param cleared true if also cleared already
   * @return win pixel URL or empty string
   */
  bidWon (bid, cleared = false) {
    const auctions = this._pendingAuctions[bid.auctionId]
    for (const auction of auctions) {
      const q = auction.markBidWon(bid, cleared)
      if (q !== null) {
        return this._remoteUrl('imp-cleared') + '&' + q
      }
    }

    return null
  }

  _onAuctionEnd (auctionId, zoneId) {
    const auctions = this._pendingAuctions[auctionId]
    const json = []

    delete this._pendingAuctions[auctionId]

    for (const auction of auctions) {
      const ajson = auction.finish()
      if (ajson !== null) {
        json.push(ajson)
      }
    }

    if (json.length === 0) {
      // return
    }

    // skip logging for now until service is done
    /* ajax(this._remoteUrl('auction-end'), json).then(() => {
      Log.debug('Successfully stored bids')
    }).catch(e => {
      Log.warn('Failed logging bids, http status ' + e.status)
    }) */
  }

  register (pbjs) {
    pbjs.onEvent('auctionInit', this._auctionInitHandler)
    pbjs.onEvent('bidRequested', this._bidRequestedHandler)
    pbjs.onEvent('bidResponse', this._bidResponseHandler)
    pbjs.onEvent('bidAdjustment', this._bidAdjustmentHandler)
    pbjs.onEvent('bidTimeout', this._bidTimeoutHandler)
  }

  deregister (pbjs) {
    pbjs.offEvent('auctionInit', this._auctionInitHandler)
    pbjs.offEvent('bidRequested', this._bidRequestedHandler)
    pbjs.offEvent('bidResponse', this._bidResponseHandler)
    pbjs.offEvent('bidAdjustment', this._bidAdjustmentHandler)
    pbjs.offEvent('bidTimeout', this._bidTimeoutHandler)
  }
}
